import React, { useEffect, useState } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Divider, IconButton } from '@mui/material';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import { Img } from '../../patients/display/Img';
import { downloadFile } from '../../../api/uploadService';
import { triggerDownload } from '../../../utils/triggerDownload';
import { bytesToMegaBytes } from "../../../utils/bytestoMegaBytes";
import { usePetImageGalleryFiles } from '../../../hooks/uploads/usePetImageGalleryFiles';
import { timeAgo } from '../../../utils/timeAgo';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VerticalHamburgerMenu from '../controls/VerticalHamburgerMenu';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useDeleteFile } from '../../../hooks/uploads/useDeleteFile';
import FileRenameForm from '../forms/FileRenameForm';

export default function ImageGallery ({ pet, open, parentTrigger, onClose, title = "Image Gallery", attachments = null }) {
    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();

    const handleClose = () => {
      onClose();
    };

    const [formOpen, setFormOpen] = React.useState(false);
    const [selectedAttachment, setSelectedAttachment] = React.useState(null);

    const [trigger, setTrigger] = React.useState(false);
    // Toggle function
    const toggleTrigger = () => {
        setTrigger(prev => !prev);
    };

    const { items, loading, loadMore, searchFunction, resetParent, reorderList, updateItem, removeItem } = usePetImageGalleryFiles(pet.id, trigger, 1, 10);

    const handleDownloadClick = async (attachment) => {
      const fileData = await downloadFile(attachment.id);
      triggerDownload(fileData, attachment.filename);
    };

    const onFileDeleteSuccess = () => {
        showSnackbar(t('files.messages.delete_success'), "success");
        toggleTrigger();
    };
    
    const { remove } = useDeleteFile(onFileDeleteSuccess);

    const handleDeleteFile = async (fileId) => {
        try {
            await remove(fileId);
        } catch (err) {
            showSnackbar(t('files.messages.delete_fail'), "error");
        }
    };

    const onFileRenameSuccess = (updatedAttachment) => {
      setFormOpen(false);
      updateItem(updatedAttachment); // ✅ patch local state
      showSnackbar(t("files.messages.rename_success"), "success");
    };

    const handleRenameClick = (attachment) => {
      setSelectedAttachment(attachment);
      setFormOpen(true);
    };

    React.useEffect(() => {
      toggleTrigger();
    }, [parentTrigger]);

    return (
      <>
      <Dialog onClose={handleClose} open={open} disableRestoreFocus>
        <DialogTitle>
          <Stack spacing={2}>
            <>
            <Typography variant="h6" sx={{margin: 0, lineHeight: 1}}>{title}</Typography>
            <Typography variant="h5" sx={{margin: 0, lineHeight: 0}}>{`${pet.name} (${pet.owner.last_name}, ${pet.owner.first_name})`}</Typography>
            </>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {items?.data?.map((attachment) => (
            <div key={attachment.id}>
              <Box
                sx={{
                position: 'relative', // parent must be relative for absolute children
                borderRadius: 2,
                overflow: 'hidden', // Ensures the image inside is clipped
                display: 'inline-block', // keeps Box sized to its content
                boxShadow: 8,
                width: "100%"
                }}
              >
                  <Img 
                    fileId={attachment.id} 
                    ratio="16:9" 
                    alt="Pet photo" 
                  />
                  <Typography 
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        bottom: 30,
                        left: 8,
                        bgcolor: 'rgba(0,0,0,0.3)',
                        color: 'white',
                        px: 1,
                        borderRadius: 1,
                    }}
                    noWrap={true}>
                      {attachment.filename}
                  </Typography>
                  <Typography 
                    variant='caption' 
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        bgcolor: 'rgba(0,0,0,0.3)',
                        color: 'white',
                        px: 1,
                        borderRadius: 1,
                    }} 
                    noWrap={true}>
                      {`uploaded ${timeAgo(attachment.createdAt)}`}
                  </Typography>
                  <Box
                  sx={{
                      position: 'absolute',
                      bottom: 8,   // adjust spacing from bottom
                      right: 8,    // adjust spacing from right
                  }}
                  >
                  <VerticalHamburgerMenu 
                      options={[
                      { 
                          id: 2, 
                          name: 'Download',
                          callback: () => handleDownloadClick(attachment)
                      },
                      { 
                          id: 3, 
                          name: 'Rename',
                          callback: () => handleRenameClick(attachment)
                      },
                      {
                          id: 4,
                          name: 'Delete',
                          callback: () => handleDeleteFile(attachment.id),
                          confirmMessage: t('files.messages.delete_confirm', {name: attachment.filename}),
                      }
                      ]}
                  />
                  </Box>
              </Box>
              
              <Divider sx={{my: 1, opacity: 0}}/>
            </div>
          ))}
          {/* Load More only shows if more pages exist */}
          {items?.pagination?.page < items?.pagination?.totalPages && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <IconButton 
                      onClick={loadMore}
                      sx={{
                          width: '100%',              // fill container width
                          justifyContent: 'center',   // center the icon horizontally
                          color: 'primary.main',      // use theme's primary color
                      }}
                  >
                      <KeyboardArrowDownIcon />
                  </IconButton>
              </Box>
          )}
        </DialogContent>
        <DialogActions>
          <div style={{flex: '1 0 0'}} />
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <FileRenameForm 
        successCallback={onFileRenameSuccess}
        attachment={selectedAttachment}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
      />
      </>
    );
}